import { Dialog, DialogContent, Grid, TextField } from '@mui/material';
import React, { useEffect } from 'react';
import { EEA2Service } from '../../../../Component/Api/EmploymentEquity/WorkforceProfile_EEA2/EEA2Service';
import { MDBox } from '../../../../Component/Common/Components/MDBox';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { WorkforceProfileDto } from '../../../../Component/Model/Dto/EmploymentEquity/WorkforceProfile_EEA2/WorkforceProfile';
import { Gender, RaceTypes, UserActivityActionType } from '../../../../Component/Model/Enum/BEE123Enums';
import { CustomDialogTitle } from '../../../Common/Custom/CustomDialogTitle';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useRecoilValue } from 'recoil';
import { EEScorecardAtom } from '../../../../State/EEScorecard.Atom';
import { toast } from 'react-toastify';
import { HeadcountSummaryTable } from '../../../../Component/Common/Components/Table/HeadcountSummaryTable';
import { EEOccupationalLevel } from '../../../../Component/Model/Enum/EmploymentEquity';
import { WorkForeProfileDrilldown } from '../../Plan/WorkforceProfile/Drilldown/WorkForceProfileDrillDown';
import { SkeletonLoading } from '../../../../Component/Common/SkeletonLoading/SkeletonLoading';
import { getDateWithoutTime } from '../../../../Helpers/DateFormatter';
import dayjs from 'dayjs';
import { UserActivityService } from '../../../../Component/Api/UserActivity/UserActivityService';
import { WorkforceProfileSection } from '../../../../Component/Common/Constants/UserActivityTrackingSections';
import { ValidatedMobileDatePicker } from '../../../../Component/Common/Components/ValidatedMobileDatePicker/ValidatedMobileDatePicker';

interface IProps {
	scorecardId: string;
}

export const WorkforceProfile = (props: IProps) => {
	const scorecard = useRecoilValue(EEScorecardAtom);
	const [filterDate, setFilterDate] = React.useState<Date>(getDateWithoutTime(new Date(scorecard.ReportPeriodDateTo)));
	const [allEmployeeData, setAllEmployeeData] = React.useState<WorkforceProfileDto.SummarySectionDto[]>([]);
	const [disabledEmployeeData, setDisabledEmployeeData] = React.useState<WorkforceProfileDto.SummarySectionDto[]>([]);
	const [showDrillDown, setShowDrillDown] = React.useState<boolean>(false);
	const [occupationalLevel, setOccupationalLevel] = React.useState<EEOccupationalLevel>();
	const [raceType, setRaceType] = React.useState<RaceTypes>();
	const [gender, setGender] = React.useState<Gender>();
	const [excludeTemporaryEmployees, setExcludeTemporaryEmployees] = React.useState<boolean>();
	const [onlyDisabled, setOnlyDisabled] = React.useState<boolean>(false);
	const [isLoading, setIsloading] = React.useState<boolean>(false);

	const filterByDate = true;

	const fetchData = async () => {
		setIsloading(true);

		try {
			const searchParameters: WorkforceProfileDto.SummarySearchParameters = {
				scorecardId: props.scorecardId,
				onlyDisabled: false,
				filterByDate: filterByDate,
				filterDate: dayjs(filterDate).format('YYYY-MM-DDTHH:mm:ss')
			};

			let searchResult = await EEA2Service.GetWorkforceProfileHeadcountSummary(searchParameters);
			if (searchResult.data) {
				setAllEmployeeData(searchResult.data);
				UserActivityService.createActivityLog({
					section: WorkforceProfileSection,
					actionType: UserActivityActionType.View,
					actionDescription: "View workforce profile",
					scorecardId: props.scorecardId,
					requestUrl: window.location.href
				})
			}
		} catch (error: any) {
			toast.error(error.response.data);
			console.log(error);
		}

		try {
			const searchParameters: WorkforceProfileDto.SummarySearchParameters = {
				scorecardId: props.scorecardId,
				onlyDisabled: true,
				filterByDate: filterByDate,
				filterDate: dayjs(filterDate).format('YYYY-MM-DDTHH:mm:ss')
			};

			let searchResult = await EEA2Service.GetWorkforceProfileHeadcountSummary(searchParameters);
			if (searchResult.data) {
				setDisabledEmployeeData(searchResult.data);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsloading(false);
		}
	};

	const onSegmentClick = (
		occupationalLevel?: EEOccupationalLevel,
		race?: RaceTypes,
		gender?: Gender,
		excludeTemporaryEmployees?: boolean
	) => {
		setOnlyDisabled(false);
		setDrillDownValues(occupationalLevel, race, gender, excludeTemporaryEmployees);
	};

	const onOnlyDisabledSegmentClick = (
		occupationalLevel?: EEOccupationalLevel,
		race?: RaceTypes,
		gender?: Gender,
		excludeTemporaryEmployees?: boolean
	) => {
		setOnlyDisabled(true);
		setDrillDownValues(occupationalLevel, race, gender, excludeTemporaryEmployees);
	};
	const setDrillDownValues = (
		occupationalLevel?: EEOccupationalLevel,
		race?: RaceTypes,
		gender?: Gender,
		excludeTemporaryEmployees?: boolean
	) => {
		setOccupationalLevel(occupationalLevel);
		setRaceType(race);
		setGender(gender);
		setShowDrillDown(true);
		setExcludeTemporaryEmployees(excludeTemporaryEmployees);
	};

	useEffect(() => {
		fetchData();
	}, [filterDate]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<MDBox>
				<Grid container>
					<MDBox mb={2} mt={2} display='flex'>
						<MDBox mt={0.5}>
							<MDTypography variant='button' fontWeight='regular' mr={2}>
								Select Workforce Profile Date
							</MDTypography>
						</MDBox>
						<LocalizationProvider dateAdapter={AdapterDateFns}>
							<ValidatedMobileDatePicker
								inputFormat='yyyy/MM/dd'
								mask='____/__/__'
								label={'Workforce Profile Date'}
								value={filterDate}
								onChange={(date: any) => setFilterDate(getDateWithoutTime(date))}
								renderInput={(params: any) => <TextField {...params} />}
								views={['year', 'month', 'day']}
							/>
						</LocalizationProvider>
					</MDBox>
				</Grid>

				<MDBox style={{ backgroundColor: 'white', padding: '15px', paddingTop: '10px' }}>
					<MDTypography color='info' fontWeight='bold' style={{ paddingBottom: '15px' }}>
						All Employees
					</MDTypography>
                    {
                        (!isLoading && allEmployeeData?.length > 0)
                        ? <HeadcountSummaryTable tableData={allEmployeeData} onSegmentClick={onSegmentClick}/>
                        : <SkeletonLoading tableLoading/>
                    }
				</MDBox>

				<MDBox mt={3} style={{ backgroundColor: 'white', padding: '15px', paddingTop: '10px' }}>
					<MDTypography color='info' fontWeight='bold' style={{ paddingBottom: '15px' }}>
						Disabled Employees
					</MDTypography>
                    {
                        (!isLoading && disabledEmployeeData?.length > 0)
                        ? <HeadcountSummaryTable tableData={disabledEmployeeData} onSegmentClick={onOnlyDisabledSegmentClick}/>
                        : <SkeletonLoading tableLoading/>
                    }
				</MDBox>
			</MDBox>

			<Dialog open={showDrillDown} onClose={() => setShowDrillDown(false)} aria-labelledby='form-dialog-title' maxWidth='xl'>
				<CustomDialogTitle id='customized-dialog-title' onClose={() => setShowDrillDown(false)}>
					Employee Structure
				</CustomDialogTitle>
				<DialogContent>
					<WorkForeProfileDrilldown
						scorecardId={props.scorecardId}
						race={raceType!}
						gender={gender!}
						occupationalLevel={occupationalLevel!}
						isDisabled={onlyDisabled}
						date={filterDate}
						excludeTemporaryEmployees={excludeTemporaryEmployees}
						section={WorkforceProfileSection}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
};
